import { useState } from 'react';
import { SearchableSelect } from '@/shared/components';
import { useDealerConfigOptionsComboboxQuery } from '../hooks/useDealerConfigOptionsComboboxQuery';
import type { DealerConfigOption, DealerConfigOptionDataType } from '../types/dealerConfigOption.types';

interface Props {
    value: DealerConfigOption | null;
    onChange: (next: DealerConfigOption | null) => void;
    placeholder?: string;
    excludeIds?: string[];
    excludeDataTypes?: DealerConfigOptionDataType[];
    disabled?: boolean;
    id?: string;
    /** Optional catalogue group to scope the search to. Omitted = search every group. */
    group?: string;
}

const optionLabel = (o: DealerConfigOption): string =>
    o.label ? `${o.label} (${o.key_name})` : o.key_name;

/**
 * Dealer catalog config-option search, built on the shared `SearchableSelect`.
 * Mirrors `smr-admin/components/ConfigOptionCombobox.tsx` structurally: this
 * wrapper owns the async data source (`useDealerConfigOptionsComboboxQuery`)
 * and the `DealerConfigOption` shape; `SearchableSelect` owns the search UX
 * (debounce, keyboard nav, a11y).
 *
 * The dealer catalog is empty by default (Epic 7.1.4 Decision 4) — an empty
 * result renders "No options match." via `SearchableSelect`'s `emptyLabel`,
 * and the caller (`DealerConfigSettingAddForm`) supports adding a setting
 * with no option selected (the null-option path).
 */
export function DealerConfigOptionCombobox({
    value,
    onChange,
    placeholder = 'Search catalog options…',
    excludeIds = [],
    excludeDataTypes = [],
    disabled = false,
    id,
    group,
}: Props) {
    const [search, setSearch] = useState('');
    const { data, isFetching } = useDealerConfigOptionsComboboxQuery({ search, page: 1, group });

    return (
        <SearchableSelect<DealerConfigOption>
            id={id}
            options={(data?.data ?? []).filter((o) => !excludeDataTypes.includes(o.data_type))}
            value={value}
            onChange={onChange}
            onSearchChange={setSearch}
            loading={isFetching}
            getOptionValue={(o) => o.id}
            getOptionLabel={optionLabel}
            excludeValues={excludeIds}
            placeholder={placeholder}
            disabled={disabled}
            clearable
            emptyLabel="No options match."
        />
    );
}
